import { type FC, isValidElement, type ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { KibChevronRightIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'
import type { Constant } from '@pbt/pbt-ui-components'

import BusinessShareIcon from '~/components/common/icons/BusinessShareIcon'
import ColorPinIcon from '~/components/common/icons/ColorPinIcon'
import RxStatusChip from '~/components/common/labels/RxStatusChip'
import { getPrescriptionStates } from '~/store/reducers/constants'
import type { RxCardItem } from '~/types'
import { getRxDisplayStatus } from '~/utils/rxStatusChipUtils'

import { ControlledSubstanceChip } from './ControlledSubstanceChip'
import { getPrescriptionTitle } from './prescriptions/rxTracker/rxCardUtilities'
import { useDoctorInfoDisplay } from './prescriptions/rxTracker/useDoctorInfoDisplay'

import styles from './RxCardHeader.module.scss'

export interface RxCardHeaderProps {
  actionNeeded?: boolean
  dateTitle?: ReactNode
  item?: RxCardItem
  onClick?: () => void
  subheader?: ReactNode
  titlePinColor?: string | null
}

const RxCardHeader: FC<RxCardHeaderProps> = ({
  actionNeeded = false,
  dateTitle,
  onClick,
  item,
  subheader,
  titlePinColor,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const prescriptionId = item?.prescription.id
  const subTypeValue =
    item?.subType && item.subType !== 'PRESCRIPTION' ? item.subType : undefined
  const searchParams = new URLSearchParams(
    subTypeValue ? { subtype: subTypeValue } : {},
  )
  const search = searchParams.toString()
  const searchQuery = search ? `?${search}` : ''
  const prescriptionDetailsPath = `/prescription/item/${prescriptionId}${searchQuery}`
  const isClickable =
    Boolean(onClick) ||
    (Boolean(prescriptionId) &&
      location.pathname !== `/prescription/item/${prescriptionId}`)

  const controlled = item?.controlled ?? false
  const businessId = item?.businessId ?? null

  const prescriptionStates = useSelector(getPrescriptionStates) as
    | Constant[]
    | undefined

  const dateLabel = item ? (
    <RxStatusChip
      prescription={item.prescription}
      prescriptionStates={prescriptionStates}
    />
  ) : null

  const doctorInfo = useDoctorInfoDisplay({
    doctor:
      item?.prescription?.doctor ??
      item?.vet ??
      item?.prescription?.doctorId ??
      undefined,
    signer: item?.prescription?.signer ?? item?.signerId ?? undefined,
    stateId: item?.prescription.stateId ?? null,
    prescription: item?.prescription ?? undefined,
  })

  const prescription = item?.prescription
  const hasStatusChip =
    prescription != null &&
    getRxDisplayStatus(
      {
        stateId: prescription.stateId,
        rejectionReason: prescription.rejectionReason,
        expirationDate: prescription.refillExpirationDate,
      },
      prescriptionStates,
    ) != null
  const hasStatusOrDoctor = hasStatusChip || Boolean(doctorInfo)

  const title = item
    ? getPrescriptionTitle(item.prescription, item, false)
    : null

  const onActivate = () => {
    if (onClick) {
      onClick()
      return
    }

    if (prescriptionId && item) {
      navigate(prescriptionDetailsPath)
    }
  }

  const hasDateInfo = Boolean(dateTitle) || Boolean(subheader)

  const header = (
    <div
      className={classNames(styles.root, {
        [styles.actionNeeded]: actionNeeded,
      })}
    >
      {hasDateInfo && (
        <div className={styles.headerRoot}>
          {dateTitle && <span className={styles.dateTitle}>{dateTitle}</span>}
          {subheader && (
            <span className={styles.headerMeta}>
              <span className={styles.dateSubheader}>{subheader}</span>
            </span>
          )}
        </div>
      )}

      {hasStatusOrDoctor && (
        <div className={styles.doctorInfo}>
          {dateLabel && <span className={styles.dateLabel}>{dateLabel}</span>}
          {doctorInfo}
        </div>
      )}

      <div className={styles.titleRow}>
        <ColorPinIcon
          alignSelf="center"
          color={titlePinColor ?? null}
          variant="big"
        />
        {title && (
          <span className={styles.titleContainer}>
            {isValidElement(title) ? (
              title
            ) : (
              <span className={styles.title}>{title}</span>
            )}
            {controlled && (
              <span className={styles.controlledSubstanceChip}>
                <ControlledSubstanceChip />
              </span>
            )}
            {businessId && (
              <span className={styles.titleBusinessIcon}>
                <BusinessShareIcon businessIds={[businessId]} />
              </span>
            )}
          </span>
        )}
        <span className={styles.actions}>
          {isClickable && <KibChevronRightIcon className={styles.chevron} />}
        </span>
      </div>
    </div>
  )

  return isClickable ? (
    <button
      className={styles.clickableHeader}
      type="button"
      onClick={onActivate}
    >
      {header}
    </button>
  ) : (
    header
  )
}

export default RxCardHeader
